import { useState } from "react";
import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { ADMIN_NAV } from "@/data/site";
import * as Icons from "lucide-react";
import { LogOut, Menu, X, ExternalLink } from "lucide-react";
import { BrandLogo } from "@/components/Header";

export default function AdminLayout() {
  const { user, logout } = useApp();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = async () => { await logout(); navigate("/admin/login"); };

  const SidebarContent = () => (
    <>
      <div className="flex items-center gap-3 px-5 h-[68px] border-b border-teal-900/50">
        <BrandLogo className="h-9" />
        <div className="leading-none"><div className="font-extrabold text-white text-sm">Admin CMS</div><div className="text-[9px] tracking-widest text-amber-300 uppercase">Operations Center</div></div>
      </div>
      <nav className="p-3 space-y-1 flex-1 overflow-y-auto">
        {ADMIN_NAV.map((n) => {
          const Icon = Icons[n.icon] || Icons.Circle;
          return (
            <NavLink key={n.path} to={n.path} end={n.path === "/admin"} onClick={() => setOpen(false)} data-testid={`admin-nav-${n.key.toLowerCase().replace(/[^a-z]/g, "-")}`}
              className={({ isActive }) => `flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium transition-all ${isActive ? "bg-white text-[#015E5C] shadow-lg shadow-teal-950/30" : "text-teal-50/85 hover:bg-white/10 hover:text-white"}`}>
              <Icon className="w-4.5 h-4.5" style={{ width: 18, height: 18 }} /> {n.key}
            </NavLink>
          );
        })}
      </nav>
      <div className="p-3 border-t border-teal-900/50 space-y-1">
        <Link to="/" target="_blank" className="flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm text-teal-50/80 hover:bg-white/10 hover:text-white"><ExternalLink className="w-4 h-4" /> View Live Site</Link>
        <button onClick={doLogout} data-testid="admin-logout" className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm text-red-200 hover:bg-red-500/20"><LogOut className="w-4 h-4" /> Sign Out</button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#FFFFFF] flex">
      {/* desktop sidebar */}
      <aside className="hidden lg:flex flex-col w-64 fixed inset-y-0 bg-[#015E5C] border-r border-teal-900/50 z-40">
        <SidebarContent />
      </aside>

      {/* mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <aside className="relative flex flex-col w-64 bg-[#015E5C] border-r border-teal-900/50"><SidebarContent /></aside>
        </div>
      )}

      <div className="flex-1 lg:ml-64 flex flex-col min-w-0">
        <header className="sticky top-0 z-30 h-[68px] bg-[#FFFFFF]/90 border-b border-teal-900/50 flex items-center justify-between px-5" style={{ backdropFilter: "blur(12px)" }}>
          <button className="lg:hidden text-white" onClick={() => setOpen(!open)}>{open ? <X /> : <Menu />}</button>
          <div className="flex items-center gap-2 text-sm text-slate-600 font-mono"><span className="w-2 h-2 rounded-full bg-teal-400 pulse-dot" /> Live Operations Center</div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block"><div className="text-sm font-semibold text-[#0B3B39]">{user?.name}</div><div className="text-[10px] uppercase tracking-wider text-amber-600">{user?.role}</div></div>
            <div className="grid place-items-center w-9 h-9 rounded-full bg-teal-700 text-white font-bold text-sm">{user?.name?.[0] || "A"}</div>
          </div>
        </header>
        <div className="p-5 sm:p-8 flex-1"><Outlet /></div>
      </div>
    </div>
  );
}
